import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import AuthShell from "./AuthShell";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";

export default function CmsLoginPage() {
    const { login, user } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({ identifier: "", password: "" });
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (!user) return;

        if (user.role === "user") {
            toast.error("You are not authorized to access the CMS.");
            nav("/admin", { replace: true });
            return;
        }

        nav("/admin/cms", { replace: true });
    }, [user, nav]);
    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(form.identifier, form.password);
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (detail?.requires_verification) {
                nav(`/verify-email?email=${encodeURIComponent(detail.email || form.identifier)}`);
                toast.error("Please verify your email before signing in.");
            } else {
                toast.error(typeof detail === "string" ? detail : "Login failed");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthShell
            title="CMS Login"
            subtitle="Sign in to manage the SQIT website"
        >
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email or username</label>
                    <input
                        data-testid={AUTH.loginIdentifier}
                        required
                        autoFocus
                        value={form.identifier}
                        onChange={(e) => setForm({ ...form, identifier: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135] transition-colors"
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Password</label>
                    <div className="relative">
                        <input
                            data-testid={AUTH.loginPassword}
                            type={showPassword ? "text" : "password"}
                            required
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#006B3C] transition-colors"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                </div>
                <div className="flex justify-between text-xs">
                    <Link data-testid={AUTH.forgotPasswordLink} to="/forgot-password" className="text-[#006B3C] hover:text-[#09582D] font-medium">
                        Forgot password?
                    </Link>
                    <Link data-testid={AUTH.forgotUsernameLink} to="/forgot-username" className="text-[#006B3C] hover:text-[#09582D] font-medium">
                        Forgot username?
                    </Link>
                </div>
                <button
                    data-testid={AUTH.loginSubmit}
                    disabled={submitting}
                    className="w-full bg-[#006B3C] text-white hover:bg-[#09582D] transition-colors rounded-md px-4 py-3 text-sm font-semibold disabled:opacity-60"
                >
                    {submitting ? "Signing in…" : "Sign in"}
                </button>
            </form>
        </AuthShell>
    );
}
